import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  LinearProgress,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import ArrowOutwardRoundedIcon from "@mui/icons-material/ArrowOutwardRounded";
import ArrowUpwardRoundedIcon from "@mui/icons-material/ArrowUpwardRounded";
import CategoryRoundedIcon from "@mui/icons-material/CategoryRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import FileDownloadRoundedIcon from "@mui/icons-material/FileDownloadRounded";
import Inventory2RoundedIcon from "@mui/icons-material/Inventory2Rounded";
import MoreHorizRoundedIcon from "@mui/icons-material/MoreHorizRounded";
import NotificationsNoneRoundedIcon from "@mui/icons-material/NotificationsNoneRounded";
import PrintRoundedIcon from "@mui/icons-material/PrintRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import ShoppingCartCheckoutRoundedIcon from "@mui/icons-material/ShoppingCartCheckoutRounded";
import TimelineRoundedIcon from "@mui/icons-material/TimelineRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import ConnString from "../../../ConnString.js";

const Connstring2 = ConnString.connstring;




const categoryColors = ["#6366f1", "#06b6d4", "#10b981", "#f59e0b", "#f43f5e", "#8b5cf6"];
const productColors = ["#6366f1", "#f59e0b", "#10b981", "#f43f5e", "#0ea5e9"];

const formatNumber = (value) => Number(value || 0).toLocaleString();
const formatCurrency = (value) => Number(value || 0).toLocaleString("en-PH", {
  style: "currency",
  currency: "PHP",
  maximumFractionDigits: 2,
});
const toApiDate = (date) => date.toISOString().slice(0, 10);
const createDateRange = (selectedPeriod = "Last 30 days") => {
  const days = selectedPeriod === "Last 7 days" ? 7 : selectedPeriod === "Last 90 days" ? 90 : 30;
  const dateTo = new Date();
  const dateFrom = new Date(dateTo);
  dateFrom.setDate(dateFrom.getDate() - (days - 1));
  return { dateFrom: toApiDate(dateFrom), dateTo: toApiDate(dateTo) };
};
const isIncomingMovement = (movementType) => ["IN", "BEGBAL"].includes(String(movementType || "").toUpperCase());
const escapeCsv = (value) => `"${String(value ?? "").replace(/"/g, '""')}"`;
const escapeHtml = (value) => String(value ?? "")
  .replace(/&/g, "&amp;")
  .replace(/</g, "&lt;")
  .replace(/>/g, "&gt;")
  .replace(/"/g, "&quot;");

const DashboardInvty = () => {
  const theme = useTheme();
  const dark = theme.palette.mode === "dark";
  const [period, setPeriod] = useState("Last 30 days");
  const [dateRange, setDateRange] = useState(() => createDateRange("Last 30 days"));
  const [query, setQuery] = useState("");
  const [summary, setSummary] = useState({});
  const [inventory, setInventory] = useState([]);
  const [categoryRows, setCategoryRows] = useState([]);
  const [movements, setMovements] = useState([]);
  const [salesRows, setSalesRows] = useState([]);
  const [salesCategoryCode, setSalesCategoryCode] = useState("");
  const [branches, setBranches] = useState([]);
  const [branchCode, setBranchCode] = useState("");
  const [warehouses, setWarehouses] = useState([]);
  const [warehouseCode, setWarehouseCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [lowStockOpen, setLowStockOpen] = useState(false);
  const [inventoryValueOpen, setInventoryValueOpen] = useState(false);
  const [movementItem, setMovementItem] = useState(null);
  const [itemMovements, setItemMovements] = useState([]);
  const [movementLoading, setMovementLoading] = useState(false);
  const [movementError, setMovementError] = useState("");
  const [movementDateFrom, setMovementDateFrom] = useState("");
  const [movementDateTo, setMovementDateTo] = useState("");
  const [movementWarehouseCode, setMovementWarehouseCode] = useState("");
  const [stockItem, setStockItem] = useState(null);
  const [stockItemInfo, setStockItemInfo] = useState(null);
  const [warehouseStocks, setWarehouseStocks] = useState([]);
  const [warehouseStockLoading, setWarehouseStockLoading] = useState(false);
  const [warehouseStockError, setWarehouseStockError] = useState("");
  const compID = sessionStorage.getItem("compID");
  const userID = sessionStorage.getItem("userID");

  const changeDashboardPeriod = (selectedPeriod) => {
    setPeriod(selectedPeriod);
    if (selectedPeriod !== "Custom") setDateRange(createDateRange(selectedPeriod));
  };

  const changeDashboardDate = (field, value) => {
    setPeriod("Custom");
    setDateRange((current) => ({ ...current, [field]: value }));
  };

  useEffect(() => {
    if (!compID || !userID) {
      setError("Company or user information is missing. Please sign in again.");
      return;
    }

    let alive = true;
    const fetchBranches = async () => {
      try {
        const res = await fetch(`${Connstring2}/api/userBranchAccess`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ companyID: compID, currentuserID: userID }),
        });
        const data = await res.json();
        if (!res.ok || data.success === false) throw new Error(data.message || "Unable to load branches.");
        if (!alive) return;
        const options = Array.isArray(data.options) ? data.options : [];
        setBranches(options);
        setBranchCode((current) => current || String(options[0]?.Branch || options[0]?.id || ""));
        if (!options.length) setError("No branch access is assigned to this user.");
      } catch (e) {
        if (alive) setError(e.message || "Unable to load branches.");
      }
    };
    fetchBranches();
    return () => { alive = false; };
  }, [compID, userID]);

  useEffect(() => {
    if (!compID || !branchCode) {
      setWarehouses([]);
      setWarehouseCode("");
      return;
    }

    const controller = new AbortController();
    const fetchWarehouses = async () => {
      try {
        const res = await fetch(`${Connstring2}/api/Warehouse`, {
          method: "POST",
          credentials: "include",
          signal: controller.signal,
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ compID, branchCode, q: "" }),
        });
        const data = await res.json();
        if (!res.ok || data.success === false) throw new Error(data.message || "Unable to load warehouses.");
        setWarehouses(Array.isArray(data.options) ? data.options : []);
        setWarehouseCode("");
      } catch (e) {
        if (e.name !== "AbortError") setError(e.message || "Unable to load warehouses.");
      }
    };
    fetchWarehouses();
    return () => controller.abort();
  }, [compID, branchCode]);

  useEffect(() => {
    if (!compID || !branchCode) return;
    if (!dateRange.dateFrom || !dateRange.dateTo || dateRange.dateFrom > dateRange.dateTo) {
      setError("Please select a valid dashboard date range.");
      return;
    }
    const controller = new AbortController();

    const fetchDashboard = async () => {
      try {
        setLoading(true);
        setError("");
        const res = await fetch(`${Connstring2}/api/Inventory/Dashboard`, {
          method: "POST",
          credentials: "include",
          signal: controller.signal,
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            compID,
            branchCode,
            warehouseCode,
            salesCategoryCode,
            dateFrom: dateRange.dateFrom,
            dateTo: dateRange.dateTo,
          }),
        });
        const dashboard = await res.json();
        if (!res.ok || dashboard.success === false) throw new Error(dashboard.message || `API error: ${res.status}`);

        setSummary(dashboard.summary || {});
        setInventory(Array.isArray(dashboard.items) ? dashboard.items : []);
        setCategoryRows(Array.isArray(dashboard.categories) ? dashboard.categories : []);
        setMovements(Array.isArray(dashboard.movements) ? dashboard.movements : []);
        setSalesRows(Array.isArray(dashboard.sales) ? dashboard.sales : []);
      } catch (e) {
        if (e.name !== "AbortError") {
          setError(e.message || "Failed to load inventory dashboard.");
          setSummary({});
          setInventory([]);
          setCategoryRows([]);
          setMovements([]);
          setSalesRows([]);
        }
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };
    fetchDashboard();
    return () => controller.abort();
  }, [compID, branchCode, warehouseCode, salesCategoryCode, dateRange]);

  const filteredInventory = useMemo(() => {
    const search = query.trim().toLowerCase();
    if (!search) return inventory;
    return inventory.filter((item) => `${item.name} ${item.sku} ${item.category}`.toLowerCase().includes(search));
  }, [inventory, query]);

  const lowStockItems = useMemo(
    () => inventory
      .filter((item) => Number(item.stock || 0) <= 20)
      .sort((a, b) => Number(a.stock || 0) - Number(b.stock || 0)),
    [inventory]
  );

  const inventoryValueItems = useMemo(
    () => [...inventory].sort((a, b) => Number(b.inventoryValue || 0) - Number(a.inventoryValue || 0)),
    [inventory]
  );

  const displayedInventoryValue = useMemo(
    () => inventoryValueItems.reduce((total, item) => total + Number(item.inventoryValue || 0), 0),
    [inventoryValueItems]
  );

  const categories = useMemo(() => {
    const maximum = Math.max(...categoryRows.map((item) => Number(item.stock || 0)), 1);
    return categoryRows.slice(0, 6).map((item, index) => ({
      label: item.category || item.CategoryCode || "Uncategorized",
      value: Math.max(0, (Number(item.stock || 0) / maximum) * 100),
      count: formatNumber(item.stock),
      color: categoryColors[index % categoryColors.length],
    }));
  }, [categoryRows]);

  const sales = useMemo(() => {
    const maximum = Math.max(...salesRows.map((item) => Number(item.sales || 0)), 1);
    return salesRows.slice(0, 10).map((item, index) => ({
      name: item.name || "Unknown",
      quantity: Number(item.sales || 0),
      percentage: Math.max(0, (Number(item.sales || 0) / maximum) * 100),
      color: productColors[index % productColors.length],
    }));
  }, [salesRows]);

  const selectedSalesCategoryName = useMemo(() => {
    if (!salesCategoryCode) return "All categories";
    const category = categoryRows.find((item) => String(item.CategoryCode) === salesCategoryCode);
    return category?.category || salesCategoryCode;
  }, [categoryRows, salesCategoryCode]);

  const chart = useMemo(() => {
    const values = movements.map((item) => Number(item.received || 0) - Number(item.dispatched || 0));
    const max = Math.max(...values.map(Math.abs), 1);
    const width = 605;
    const height = 130;
    const points = values.map((value, index) => {
      const x = values.length <= 1 ? width / 2 : (index / (values.length - 1)) * width;
      const y = height - ((value + max) / (max * 2)) * height;
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    }).join(" ");
    return { points, max, labels: movements.map((item) => item.movementDate) };
  }, [movements]);

  const selectedBranchName = useMemo(() => {
    const branch = branches.find((item) => String(item.Branch || item.id) === branchCode);
    return branch?.BranchName || branch?.LocName || branchCode || "All branches";
  }, [branches, branchCode]);

  const selectedWarehouseName = useMemo(() => {
    if (!warehouseCode) return "All warehouses";
    const warehouse = warehouses.find((item) => String(item.Code) === warehouseCode);
    return warehouse?.Description ? `[${warehouseCode}] ${warehouse.Description}` : warehouseCode;
  }, [warehouses, warehouseCode]);

  const movementWarehouseName = useMemo(() => {
    if (!movementWarehouseCode) return "All warehouses";
    const warehouse = warehouses.find((item) => String(item.Code) === movementWarehouseCode);
    return warehouse?.Description ? `[${movementWarehouseCode}] ${warehouse.Description}` : movementWarehouseCode;
  }, [warehouses, movementWarehouseCode]);

  const warehouseStockTotals = useMemo(() => warehouseStocks.reduce((totals, warehouse) => ({
    stock: totals.stock + Number(warehouse.stock || 0),
    value: totals.value + Number(warehouse.inventoryValue || 0),
  }), { stock: 0, value: 0 }), [warehouseStocks]);

  const viewWarehouseStock = async (item) => {
    setStockItem(item);
    setStockItemInfo(null);
    setWarehouseStocks([]);
    setWarehouseStockError("");
    setWarehouseStockLoading(true);

    try {
      const res = await fetch(`${Connstring2}/api/Inventory/WarehouseStock`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          compID,
          branchCode,
          itemCode: item.sku,
          dateFrom: dateRange.dateFrom,
          dateTo: dateRange.dateTo,
        }),
      });
      const data = await res.json();
      if (!res.ok || data.success === false) throw new Error(data.message || "Unable to load warehouse stock information.");
      setStockItemInfo(data.item || null);
      setWarehouseStocks(Array.isArray(data.warehouses) ? data.warehouses : []);
    } catch (e) {
      setWarehouseStockError(e.message || "Unable to load warehouse stock information.");
    } finally {
      setWarehouseStockLoading(false);
    }
  };

  const fetchStockMovement = async (item, dateFrom, dateTo, selectedMovementWarehouse) => {
    setItemMovements([]);
    setMovementError("");
    setMovementLoading(true);

    try {
      const res = await fetch(`${Connstring2}/api/Inventory/StockMovement`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          compID,
          branchCode,
          warehouseCode: selectedMovementWarehouse,
          itemCode: item.sku,
          dateFrom,
          dateTo,
        }),
      });
      const data = await res.json();
      if (!res.ok || data.success === false) throw new Error(data.message || "Unable to load stock movements.");
      setItemMovements(Array.isArray(data.movements) ? data.movements : []);
    } catch (e) {
      setMovementError(e.message || "Unable to load stock movements.");
    } finally {
      setMovementLoading(false);
    }
  };

  const viewStockMovement = (item) => {
    setMovementItem(item);
    setMovementDateFrom(dateRange.dateFrom);
    setMovementDateTo(dateRange.dateTo);
    setMovementWarehouseCode(warehouseCode);
    fetchStockMovement(item, dateRange.dateFrom, dateRange.dateTo, warehouseCode);
  };

  const updateStockMovementDates = () => {
    if (!movementDateFrom || !movementDateTo) {
      setMovementError("Please select both a start date and an end date.");
      return;
    }
    if (movementDateFrom > movementDateTo) {
      setMovementError("The start date cannot be later than the end date.");
      return;
    }
    if (movementItem) fetchStockMovement(movementItem, movementDateFrom, movementDateTo, movementWarehouseCode);
  };

  const updateMovementWarehouse = (selectedCode) => {
    setMovementWarehouseCode(selectedCode);
    if (movementItem && movementDateFrom && movementDateTo) {
      fetchStockMovement(movementItem, movementDateFrom, movementDateTo, selectedCode);
    }
  };

  const printStockMovement = () => {
    if (!movementItem || !itemMovements.length) return;

    const printWindow = window.open("", "_blank", "width=1000,height=760");
    if (!printWindow) {
      setMovementError("Please allow pop-ups to print the stock movement report.");
      return;
    }

    const totalIn = itemMovements.reduce(
      (total, movement) => total + (isIncomingMovement(movement.movementType) ? Number(movement.quantity || 0) : 0),
      0
    );
    const totalOut = itemMovements.reduce(
      (total, movement) => total + (movement.movementType === "OUT" ? Number(movement.quantity || 0) : 0),
      0
    );
    const rows = itemMovements.map((movement) => {
      const incoming = isIncomingMovement(movement.movementType);
      return `<tr>
        <td>${escapeHtml(new Date(`${movement.movementDate}T00:00:00`).toLocaleDateString())}</td>
        <td>${escapeHtml(movement.documentNumber || "—")}</td>
        <td>${escapeHtml(movement.tradeName || "—")}</td>
        <td>${escapeHtml(movement.warehouse || "—")}</td>
        <td class="${incoming ? "incoming" : "outgoing"}">${incoming ? "Stock in" : "Stock out"}</td>
        <td class="number ${incoming ? "incoming" : "outgoing"}">${incoming ? "+" : "-"}${escapeHtml(formatNumber(movement.quantity))}</td>
        <td class="number">${escapeHtml(formatCurrency(movement.unitCost))}</td>
      </tr>`;
    }).join("");

    printWindow.document.write(`<!doctype html><html><head><title>Stock Movement - ${escapeHtml(movementItem.sku)}</title><style>
      @page{size:A4 landscape;margin:14mm}*{box-sizing:border-box}body{font-family:Arial,sans-serif;color:#172033;margin:0}h1{font-size:24px;margin:0 0 5px}.product{font-size:15px;font-weight:700;margin-bottom:5px}.meta{color:#64748b;font-size:12px;margin-bottom:18px}.summary{display:grid;grid-template-columns:repeat(3,1fr);gap:10px;margin-bottom:20px}.stat{border:1px solid #dfe4ec;border-radius:8px;padding:12px}.stat small{color:#64748b}.stat strong{display:block;font-size:19px;margin-top:5px}table{width:100%;border-collapse:collapse;font-size:11px}th{background:#f1f5f9;text-align:left;padding:9px;border-bottom:1px solid #cbd5e1}td{padding:8px 9px;border-bottom:1px solid #e2e8f0}.number{text-align:right}.incoming{color:#047857;font-weight:700}.outgoing{color:#be123c;font-weight:700}.footer{color:#94a3b8;font-size:9px;margin-top:14px}@media print{body{-webkit-print-color-adjust:exact;print-color-adjust:exact}}
    </style></head><body>
      <h1>Stock Movement Report</h1>
      <div class="product">${escapeHtml(movementItem.name)} · ${escapeHtml(movementItem.sku)}</div>
      <div class="meta">${escapeHtml(selectedBranchName)} &nbsp;·&nbsp; ${escapeHtml(movementWarehouseName)} &nbsp;·&nbsp; ${escapeHtml(movementDateFrom)} to ${escapeHtml(movementDateTo)}</div>
      <div class="summary">
        <div class="stat"><small>Stock in</small><strong>${escapeHtml(formatNumber(totalIn))}</strong></div>
        <div class="stat"><small>Stock out</small><strong>${escapeHtml(formatNumber(totalOut))}</strong></div>
        <div class="stat"><small>Net movement</small><strong>${escapeHtml(formatNumber(totalIn - totalOut))}</strong></div>
      </div>
      <table><thead><tr><th>Date</th><th>Document No.</th><th>Name</th><th>Warehouse</th><th>Movement</th><th class="number">Quantity</th><th class="number">Unit Cost</th></tr></thead><tbody>${rows}</tbody></table>
      <div class="footer">Generated ${escapeHtml(new Date().toLocaleString("en-PH"))}</div>
      <script>window.onload=()=>{window.print();window.onafterprint=()=>window.close();};</script>
    </body></html>`);
    printWindow.document.close();
  };

  const printSalesReport = () => {
    if (!sales.length) return;
    const printWindow = window.open("", "_blank", "width=900,height=760");
    if (!printWindow) {
      setError("Please allow pop-ups to print the sales report.");
      return;
    }

    const rows = sales.map((sale, index) => `<tr><td>${index + 1}</td><td>${escapeHtml(sale.name)}</td><td class="number">${escapeHtml(formatNumber(sale.quantity))}</td></tr>`).join("");
    const total = sales.reduce((sum, sale) => sum + sale.quantity, 0);
    printWindow.document.write(`<!doctype html><html><head><title>Top 10 Sales</title><style>
      @page{size:A4 portrait;margin:16mm}*{box-sizing:border-box}body{font-family:Arial,sans-serif;color:#172033;margin:0}h1{font-size:24px;margin:0 0 5px}.meta{color:#64748b;font-size:12px;line-height:1.6;margin-bottom:20px}.summary{border:1px solid #dfe4ec;border-radius:8px;padding:12px;margin-bottom:18px}.summary small{color:#64748b}.summary strong{display:block;font-size:20px;margin-top:4px}table{width:100%;border-collapse:collapse;font-size:12px}th{background:#f1f5f9;text-align:left;padding:10px;border-bottom:1px solid #cbd5e1}td{padding:10px;border-bottom:1px solid #e2e8f0}.number{text-align:right}.footer{color:#94a3b8;font-size:9px;margin-top:14px}@media print{body{-webkit-print-color-adjust:exact;print-color-adjust:exact}}
    </style></head><body><h1>Top 10 Sales</h1><div class="meta">${escapeHtml(selectedBranchName)} · ${escapeHtml(selectedWarehouseName)}<br>${escapeHtml(selectedSalesCategoryName)} · ${escapeHtml(dateRange.dateFrom)} to ${escapeHtml(dateRange.dateTo)}</div><div class="summary"><small>Total units among top 10</small><strong>${escapeHtml(formatNumber(total))}</strong></div><table><thead><tr><th>Rank</th><th>Name</th><th class="number">Units Sold</th></tr></thead><tbody>${rows}</tbody></table><div class="footer">Generated ${escapeHtml(new Date().toLocaleString("en-PH"))}</div><script>window.onload=()=>{window.print();window.onafterprint=()=>window.close();};</script></body></html>`);
    printWindow.document.close();
  };

  const exportToExcel = () => {
    const rows = [
      ["Inventory Report"],
      ["Branch", selectedBranchName],
      ["Warehouse", selectedWarehouseName],
      ["Period", `${dateRange.dateFrom} to ${dateRange.dateTo}`],
      [],
      ["SKU", "Product", "Category", "Stock", "Status", "Price", "Average Cost", "Inventory Value"],
      ...inventory.map((item) => [item.sku, item.name, item.category, item.stock, item.status, item.price, item.averageCost, item.inventoryValue]),
    ];
    const csv = `\uFEFF${rows.map((row) => row.map(escapeCsv).join(",")).join("\r\n")}`;
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
    const link = document.createElement("a");
    link.href = url;
    link.download = `inventory-${branchCode || "report"}-${dateRange.dateTo}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const printReport = () => {
    const printWindow = window.open("", "_blank", "width=1100,height=800");
    if (!printWindow) {
      setError("Please allow pop-ups to print the inventory report.");
      return;
    }
    const itemRows = inventory.map((item) => `
      <tr><td>${escapeHtml(item.sku)}</td><td>${escapeHtml(item.name)}</td><td>${escapeHtml(item.category)}</td><td class="number">${escapeHtml(formatNumber(item.stock))}</td><td>${escapeHtml(item.status)}</td><td class="number">${escapeHtml(formatCurrency(item.price))}</td></tr>
    `).join("");
    printWindow.document.write(`<!doctype html><html><head><title>Inventory Report</title><style>
      @page{size:A4 landscape;margin:14mm}*{box-sizing:border-box}body{font-family:Arial,sans-serif;color:#172033;margin:0}h1{font-size:24px;margin:0 0 5px}.meta{color:#64748b;font-size:12px;margin-bottom:20px}.summary{display:grid;grid-template-columns:repeat(4,1fr);gap:10px;margin-bottom:20px}.stat{border:1px solid #dfe4ec;border-radius:8px;padding:12px}.stat small{color:#64748b}.stat strong{display:block;font-size:19px;margin-top:5px}table{width:100%;border-collapse:collapse;font-size:11px}th{background:#f1f5f9;text-align:left;padding:9px;border-bottom:1px solid #cbd5e1}td{padding:8px 9px;border-bottom:1px solid #e2e8f0}.number{text-align:right}.footer{color:#94a3b8;font-size:9px;margin-top:14px}@media print{body{-webkit-print-color-adjust:exact;print-color-adjust:exact}}
    </style></head><body><h1>Inventory Report</h1><div class="meta">${escapeHtml(selectedBranchName)} &nbsp;·&nbsp; ${escapeHtml(selectedWarehouseName)} &nbsp;·&nbsp; ${escapeHtml(dateRange.dateFrom)} to ${escapeHtml(dateRange.dateTo)}</div><div class="summary">
      <div class="stat"><small>Total inventory</small><strong>${escapeHtml(formatNumber(summary.TotalInventory))}</strong></div>
      <div class="stat"><small>Inventory value</small><strong>${escapeHtml(formatCurrency(summary.InventoryValue))}</strong></div>
      <div class="stat"><small>Low stock</small><strong>${escapeHtml(formatNumber(summary.LowStockItems))}</strong></div>
      <div class="stat"><small>Categories</small><strong>${escapeHtml(formatNumber(summary.TotalCategories))}</strong></div>
    </div><table><thead><tr><th>SKU</th><th>Product</th><th>Category</th><th class="number">Stock</th><th>Status</th><th class="number">Price</th></tr></thead><tbody>${itemRows || '<tr><td colspan="6">No inventory items available.</td></tr>'}</tbody></table><div class="footer">Generated ${escapeHtml(new Date().toLocaleString("en-PH"))}</div><script>window.onload=()=>{window.print();window.onafterprint=()=>window.close();};</script></body></html>`);
    printWindow.document.close();
  };

  const exportInventoryValueToExcel = () => {
    const rows = [
      ["Inventory Value Report"],
      ["Branch", selectedBranchName],
      ["Warehouse", selectedWarehouseName],
      ["Report Date", dateRange.dateTo],
      ["Total Items", inventoryValueItems.length],
      ["Total Inventory Value", displayedInventoryValue],
      [],
      ["SKU", "Item", "Category", "Stock", "Average Cost", "Amount (Value)"],
      ...inventoryValueItems.map((item) => [item.sku, item.name, item.category, item.stock, item.averageCost, item.inventoryValue]),
    ];
    const csv = `\uFEFF${rows.map((row) => row.map(escapeCsv).join(",")).join("\r\n")}`;
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
    const link = document.createElement("a");
    link.href = url;
    link.download = `inventory-value-${branchCode || "report"}-${dateRange.dateTo}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const printInventoryValueReport = () => {
    if (!inventoryValueItems.length) return;
    const printWindow = window.open("", "_blank", "width=1000,height=760");
    if (!printWindow) {
      setError("Please allow pop-ups to print the inventory value report.");
      return;
    }
    const itemRows = inventoryValueItems.map((item) => `
      <tr><td>${escapeHtml(item.sku)}</td><td>${escapeHtml(item.name)}</td><td>${escapeHtml(item.category || "Uncategorized")}</td><td class="number">${escapeHtml(formatNumber(item.stock))}</td><td class="number">${escapeHtml(formatCurrency(item.averageCost))}</td><td class="number value">${escapeHtml(formatCurrency(item.inventoryValue))}</td></tr>
    `).join("");
    printWindow.document.write(`<!doctype html><html><head><title>Inventory Value Report</title><style>
      @page{size:A4 landscape;margin:14mm}*{box-sizing:border-box}body{font-family:Arial,sans-serif;color:#172033;margin:0}h1{font-size:24px;margin:0 0 5px}.meta{color:#64748b;font-size:12px;margin-bottom:18px}.summary{display:flex;gap:10px;margin-bottom:18px}.stat{min-width:180px;border:1px solid #dfe4ec;border-radius:8px;padding:11px}.stat small{color:#64748b}.stat strong{display:block;font-size:19px;margin-top:4px}table{width:100%;border-collapse:collapse;font-size:11px}th{background:#e0f2fe;text-align:left;padding:9px;border-bottom:1px solid #bae6fd}td{padding:8px 9px;border-bottom:1px solid #e2e8f0}.number{text-align:right}.value{font-weight:bold;color:#0369a1}.footer{color:#94a3b8;font-size:9px;margin-top:14px}@media print{body{-webkit-print-color-adjust:exact;print-color-adjust:exact}}
    </style></head><body><h1>Inventory Value Report</h1><div class="meta">${escapeHtml(selectedBranchName)} &nbsp;·&nbsp; ${escapeHtml(selectedWarehouseName)} &nbsp;·&nbsp; As of ${escapeHtml(dateRange.dateTo)}</div><div class="summary"><div class="stat"><small>Total items</small><strong>${escapeHtml(formatNumber(inventoryValueItems.length))}</strong></div><div class="stat"><small>Total inventory value</small><strong>${escapeHtml(formatCurrency(displayedInventoryValue))}</strong></div></div><table><thead><tr><th>SKU</th><th>Item</th><th>Category</th><th class="number">Stock</th><th class="number">Average Cost</th><th class="number">Amount (Value)</th></tr></thead><tbody>${itemRows}</tbody></table><div class="footer">Generated ${escapeHtml(new Date().toLocaleString("en-PH"))}</div><script>window.onload=()=>{window.print();window.onafterprint=()=>window.close();};</script></body></html>`);
    printWindow.document.close();
  };

  const exportLowStockToExcel = () => {
    const rows = [
      ["Low-Stock Inventory Report"],
      ["Branch", selectedBranchName],
      ["Warehouse", selectedWarehouseName],
      ["Report Date", dateRange.dateTo],
      ["Low Stock", lowStockItems.filter((item) => Number(item.stock) > 0).length],
      ["Out of Stock", lowStockItems.filter((item) => Number(item.stock) <= 0).length],
      [],
      ["SKU", "Product", "Category", "Stock", "Status", "Price", "Average Cost", "Inventory Value"],
      ...lowStockItems.map((item) => [item.sku, item.name, item.category, item.stock, item.status, item.price, item.averageCost, item.inventoryValue]),
    ];
    const csv = `\uFEFF${rows.map((row) => row.map(escapeCsv).join(",")).join("\r\n")}`;
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
    const link = document.createElement("a");
    link.href = url;
    link.download = `low-stock-${branchCode || "report"}-${dateRange.dateTo}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const printLowStockReport = () => {
    const printWindow = window.open("", "_blank", "width=1000,height=760");
    if (!printWindow) {
      setError("Please allow pop-ups to print the low-stock report.");
      return;
    }
    const itemRows = lowStockItems.map((item) => `
      <tr><td>${escapeHtml(item.sku)}</td><td>${escapeHtml(item.name)}</td><td>${escapeHtml(item.category)}</td><td class="number ${Number(item.stock) <= 0 ? "danger" : "warning"}">${escapeHtml(formatNumber(item.stock))}</td><td>${escapeHtml(Number(item.stock) <= 0 ? "Out of stock" : "Low stock")}</td><td class="number">${escapeHtml(formatCurrency(item.price))}</td></tr>
    `).join("");
    printWindow.document.write(`<!doctype html><html><head><title>Low-Stock Inventory Report</title><style>
      @page{size:A4 landscape;margin:14mm}*{box-sizing:border-box}body{font-family:Arial,sans-serif;color:#172033;margin:0}h1{font-size:24px;margin:0 0 5px}.meta{color:#64748b;font-size:12px;margin-bottom:18px}.summary{display:flex;gap:10px;margin-bottom:18px}.stat{min-width:150px;border:1px solid #dfe4ec;border-radius:8px;padding:11px}.stat small{color:#64748b}.stat strong{display:block;font-size:19px;margin-top:4px}table{width:100%;border-collapse:collapse;font-size:11px}th{background:#fff7ed;text-align:left;padding:9px;border-bottom:1px solid #fed7aa}td{padding:8px 9px;border-bottom:1px solid #e2e8f0}.number{text-align:right}.warning{color:#b45309;font-weight:bold}.danger{color:#be123c;font-weight:bold}.footer{color:#94a3b8;font-size:9px;margin-top:14px}@media print{body{-webkit-print-color-adjust:exact;print-color-adjust:exact}}
    </style></head><body><h1>Low-Stock Inventory Report</h1><div class="meta">${escapeHtml(selectedBranchName)} &nbsp;·&nbsp; ${escapeHtml(selectedWarehouseName)} &nbsp;·&nbsp; As of ${escapeHtml(dateRange.dateTo)}</div><div class="summary"><div class="stat"><small>Low stock</small><strong>${lowStockItems.filter((item) => Number(item.stock) > 0).length}</strong></div><div class="stat"><small>Out of stock</small><strong>${lowStockItems.filter((item) => Number(item.stock) <= 0).length}</strong></div></div><table><thead><tr><th>SKU</th><th>Product</th><th>Category</th><th class="number">Stock</th><th>Status</th><th class="number">Price</th></tr></thead><tbody>${itemRows || '<tr><td colspan="6">No low-stock items available.</td></tr>'}</tbody></table><div class="footer">Generated ${escapeHtml(new Date().toLocaleString("en-PH"))}</div><script>window.onload=()=>{window.print();window.onafterprint=()=>window.close();};</script></body></html>`);
    printWindow.document.close();
  };
  

  const card = {
    backgroundColor: dark ? "#182033" : "#ffffff",
    border: `1px solid ${dark ? "rgba(255,255,255,.07)" : "#e9edf5"}`,
    borderRadius: "18px",
    boxShadow: dark ? "none" : "0 8px 30px rgba(15, 23, 42, .045)",
  };
  const muted = dark ? "#94a3b8" : "#64748b";
  const ink = dark ? "#f8fafc" : "#172033";

  const stats = [
    { label: "Total inventory", value: formatNumber(summary.TotalInventory), note: `${formatNumber(summary.TotalProducts)} active products`, positive: true, icon: <Inventory2RoundedIcon />, color: "#6366f1", bg: "#eef2ff" },
    { label: "Inventory value", value: formatCurrency(summary.InventoryValue), note: `As of ${dateRange.dateTo} · View details`, positive: true, action: "inventoryValue", icon: <ShoppingCartCheckoutRoundedIcon />, color: "#0ea5e9", bg: "#e0f2fe" },
    { label: "Low stock items", value: formatNumber(summary.LowStockItems), note: `${formatNumber(summary.OutOfStockItems)} out of stock · View details`, positive: false, action: "lowStock", icon: <WarningAmberRoundedIcon />, color: "#f59e0b", bg: "#fef3c7" },
    { label: "Categories", value: formatNumber(summary.TotalCategories), note: `${formatNumber(categoryRows.length)} categories returned`, positive: true, icon: <CategoryRoundedIcon />, color: "#10b981", bg: "#d1fae5" },
  ];

  return (
    <Box sx={{ minHeight: "100%", p: { xs: 2, md: 3 }, background: dark ? "#111827" : "#f7f8fc", color: ink }}>
      <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center" }} spacing={2} mb={3}>
        <Box>
          <Typography sx={{ fontSize: { xs: 24, md: 30 }, fontWeight: 800, letterSpacing: "-.7px" }}>Inventory overview</Typography>
          <Typography color={muted} fontSize={14}>Track stock levels, movement, and product performance.</Typography>
        </Box>
        <Stack direction="row" spacing={1.25} flexWrap="wrap">
          <TextField
            label="From"
            type="date"
            value={dateRange.dateFrom}
            onChange={(event) => changeDashboardDate("dateFrom", event.target.value)}
            size="small"
            InputLabelProps={{ shrink: true }}
            inputProps={{ max: dateRange.dateTo || undefined }}
            sx={{ width: 150, bgcolor: dark ? "#182033" : "#fff", borderRadius: "11px", "& .MuiOutlinedInput-root": { borderRadius: "11px", fontSize: 12 } }}
          />
          <TextField
            label="To"
            type="date"
            value={dateRange.dateTo}
            onChange={(event) => changeDashboardDate("dateTo", event.target.value)}
            size="small"
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: dateRange.dateFrom || undefined }}
            sx={{ width: 150, bgcolor: dark ? "#182033" : "#fff", borderRadius: "11px", "& .MuiOutlinedInput-root": { borderRadius: "11px", fontSize: 12 } }}
          />
          <Select value={branchCode} onChange={(e) => setBranchCode(e.target.value)} displayEmpty size="small" sx={{ minWidth: 180, bgcolor: dark ? "#182033" : "#fff", borderRadius: "11px", fontSize: 12 }}>
            {!branches.length && <MenuItem value="" disabled>No branch available</MenuItem>}
            {branches.map((branch) => (
              <MenuItem key={branch.id || branch.Branch} value={String(branch.Branch || branch.id)}>
                {branch.BranchName || `[${branch.Branch}] ${branch.LocName || ""}`}
              </MenuItem>
            ))}
          </Select>
          <Select value={warehouseCode} onChange={(e) => setWarehouseCode(e.target.value)} displayEmpty size="small" sx={{ minWidth: 190, bgcolor: dark ? "#182033" : "#fff", borderRadius: "11px", fontSize: 12 }}>
            <MenuItem value="">All warehouses</MenuItem>
            {warehouses.map((warehouse) => (
              <MenuItem key={warehouse.Code} value={String(warehouse.Code)}>
                {warehouse.Description ? `[${warehouse.Code}] ${warehouse.Description}` : warehouse.Code}
              </MenuItem>
            ))}
          </Select>
          <IconButton sx={{ ...card, width: 42, height: 42 }}><NotificationsNoneRoundedIcon /></IconButton>
          <Button disabled={loading || !inventory.length} onClick={exportToExcel} variant="outlined" startIcon={<FileDownloadRoundedIcon />} sx={{ color: dark ? "#e2e8f0" : "#334155", borderColor: dark ? "#475569" : "#d7dde7", textTransform: "none", borderRadius: "11px", px: 1.8, "&:hover": { borderColor: "#6366f1", bgcolor: dark ? "#6366f112" : "#eef2ff" } }}>Export Excel</Button>
          <Button disabled={loading} onClick={printReport} variant="outlined" startIcon={<PrintRoundedIcon />} sx={{ color: dark ? "#e2e8f0" : "#334155", borderColor: dark ? "#475569" : "#d7dde7", textTransform: "none", borderRadius: "11px", px: 1.8, "&:hover": { borderColor: "#6366f1", bgcolor: dark ? "#6366f112" : "#eef2ff" } }}>Print</Button>
          <Button variant="contained" startIcon={<AddRoundedIcon />} sx={{ bgcolor: "#5b5ce2", textTransform: "none", borderRadius: "11px", px: 2.2, boxShadow: "0 7px 18px rgba(91,92,226,.25)", "&:hover": { bgcolor: "#4f46c8" } }}>Add product</Button>
        </Stack>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2, borderRadius: "12px" }}>{error}</Alert>}
      {loading && <LinearProgress sx={{ mb: 2, borderRadius: 4, bgcolor: dark ? "#293347" : "#e8eafc", "& .MuiLinearProgress-bar": { bgcolor: "#6366f1" } }} />}

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", xl: "repeat(4, 1fr)" }, gap: 2, mb: 2 }}>
        {stats.map((stat) => (
          <Box
            key={stat.label}
            role={stat.action ? "button" : undefined}
            tabIndex={stat.action ? 0 : undefined}
            onClick={stat.action ? () => stat.action === "inventoryValue" ? setInventoryValueOpen(true) : setLowStockOpen(true) : undefined}
            onKeyDown={stat.action ? (event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                if (stat.action === "inventoryValue") setInventoryValueOpen(true);
                else setLowStockOpen(true);
              }
            } : undefined}
            sx={{
              ...card,
              p: 2.4,
              cursor: stat.action ? "pointer" : "default",
              transition: "transform .18s ease, box-shadow .18s ease, border-color .18s ease",
              ...(stat.action && {
                "&:hover": { transform: "translateY(-2px)", borderColor: stat.color, boxShadow: `0 12px 32px ${stat.color}1f` },
                "&:focus-visible": { outline: `3px solid ${stat.color}47`, outlineOffset: 2 },
              }),
            }}
          >
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
              <Avatar variant="rounded" sx={{ bgcolor: dark ? `${stat.color}22` : stat.bg, color: stat.color, width: 44, height: 44, borderRadius: "12px" }}>{stat.icon}</Avatar>
              <IconButton size="small"><MoreHorizRoundedIcon fontSize="small" /></IconButton>
            </Stack>
            <Typography color={muted} fontSize={13} mt={2}>{stat.label}</Typography>
            <Typography fontSize={27} fontWeight={800} letterSpacing="-.5px" my={0.3}>{stat.value}</Typography>
            <Stack direction="row" alignItems="center" spacing={0.4} color={stat.positive ? "#10b981" : "#f59e0b"}>
              {stat.positive ? <ArrowUpwardRoundedIcon sx={{ fontSize: 15 }} /> : <WarningAmberRoundedIcon sx={{ fontSize: 15 }} />}
              <Typography fontSize={11.5} fontWeight={700}>{stat.note}</Typography>
            </Stack>
          </Box>
        ))}
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 1.8fr) minmax(280px, .8fr)" }, gap: 2, mb: 2 }}>
        <Box sx={{ ...card, p: { xs: 2, md: 2.6 } }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2.5}>
            <Box><Typography fontSize={17} fontWeight={800}>Stock movement</Typography><Typography color={muted} fontSize={12.5}>Units received and dispatched</Typography></Box>
            <Select value={period} onChange={(e) => changeDashboardPeriod(e.target.value)} size="small" sx={{ fontSize: 12, borderRadius: "10px", "& .MuiSelect-select": { py: 1 } }}>
              {["Last 7 days", "Last 30 days", "Last 90 days", "Custom"].map((item) => <MenuItem key={item} value={item}>{item}</MenuItem>)}
            </Select>
          </Stack>
          <Box sx={{ height: 205, position: "relative", overflow: "hidden" }}>
            <Stack sx={{ position: "absolute", inset: 0 }} justifyContent="space-between">
              {[chart.max, chart.max / 2, 0, -chart.max / 2, -chart.max].map((tick, index) => <Box key={index} sx={{ borderBottom: `1px dashed ${dark ? "#334155" : "#e8ecf3"}`, height: 1 }}><Typography color={muted} fontSize={10} mt={-1.7}>{formatNumber(Math.round(tick))}</Typography></Box>)}
            </Stack>
            {chart.points ? <Box component="svg" viewBox="0 0 605 145" preserveAspectRatio="none" sx={{ position: "absolute", left: 35, right: 5, top: 25, width: "calc(100% - 40px)", height: 145 }}>
              <defs><linearGradient id="inventoryGradient" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#6366f1" stopOpacity=".28"/><stop offset="1" stopColor="#6366f1" stopOpacity="0"/></linearGradient></defs>
              <polygon points={`${chart.points} 605,145 0,145`} fill="url(#inventoryGradient)" />
              <polyline points={chart.points} fill="none" stroke="#6366f1" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
            </Box> : <Typography color={muted} fontSize={12} sx={{ position: "absolute", inset: 0, display: "grid", placeItems: "center" }}>No stock movement for this period.</Typography>}
            <Stack direction="row" justifyContent="space-between" sx={{ position: "absolute", bottom: 0, left: 35, right: 2 }}>
              {chart.labels.filter((_, index) => index % Math.max(1, Math.ceil(chart.labels.length / 6)) === 0).slice(0, 6).map((date) => <Typography key={date} color={muted} fontSize={10}>{new Date(`${date}T00:00:00`).toLocaleDateString(undefined, { month: "short", day: "2-digit" })}</Typography>)}
            </Stack>
          </Box>
        </Box>

        <Box sx={{ ...card, p: 2.6 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2.4}>
            <Box><Typography fontSize={17} fontWeight={800}>Stock by category</Typography><Typography color={muted} fontSize={12.5}>Current distribution</Typography></Box>
            <ArrowOutwardRoundedIcon sx={{ color: muted, fontSize: 20 }} />
          </Stack>
          <Stack spacing={2.15}>
            {categories.map((category) => (
              <Box key={category.label}>
                <Stack direction="row" justifyContent="space-between" mb={0.8}>
                  <Stack direction="row" alignItems="center" spacing={1}><Box sx={{ width: 9, height: 9, borderRadius: "3px", bgcolor: category.color }} /><Typography fontSize={12.5} fontWeight={700}>{category.label}</Typography></Stack>
                  <Typography fontSize={12} color={muted}>{category.count}</Typography>
                </Stack>
                <LinearProgress variant="determinate" value={category.value} sx={{ height: 7, borderRadius: 5, bgcolor: dark ? "#293347" : "#eef1f6", "& .MuiLinearProgress-bar": { bgcolor: category.color, borderRadius: 5 } }} />
              </Box>
            ))}
            {!categories.length && <Typography color={muted} fontSize={12} textAlign="center" py={5}>No category stock available.</Typography>}
          </Stack>
        </Box>
      </Box>

      <Box sx={{ ...card, p: { xs: 2, md: 2.6 }, mb: 2 }}>
        <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center" }} spacing={1} mb={2.5}>
          <Box><Typography fontSize={17} fontWeight={800}>Sales</Typography><Typography color={muted} fontSize={12.5}>Top 10 names by units sold · {selectedWarehouseName}</Typography></Box>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ xs: "stretch", sm: "center" }}>
            <Select value={salesCategoryCode} onChange={(event) => setSalesCategoryCode(event.target.value)} displayEmpty size="small" sx={{ minWidth: 190, borderRadius: "10px", fontSize: 12 }}>
              <MenuItem value="">All categories</MenuItem>
              {categoryRows.map((category) => <MenuItem key={category.CategoryCode} value={String(category.CategoryCode)}>{category.category || category.CategoryCode}</MenuItem>)}
            </Select>
            <Chip label={`${dateRange.dateFrom} to ${dateRange.dateTo}`} size="small" sx={{ color: muted, fontSize: 10.5, bgcolor: dark ? "#202a3d" : "#f1f5f9" }} />
            <Button disabled={loading || !sales.length} onClick={printSalesReport} size="small" variant="outlined" startIcon={<PrintRoundedIcon />} sx={{ textTransform: "none", borderRadius: "9px" }}>Print</Button>
          </Stack>
        </Stack>
        <Stack spacing={1.7}>
          {sales.map((sale, index) => (
            <Box key={`${sale.name}-${index}`} sx={{ display: "grid", gridTemplateColumns: { xs: "28px minmax(100px, 1fr)", sm: "28px minmax(150px, 1.1fr) minmax(240px, 3fr) 80px" }, alignItems: "center", columnGap: 1.5, rowGap: 0.7 }}>
              <Typography color={muted} fontSize={11} fontWeight={800}>#{index + 1}</Typography>
              <Typography fontSize={12.5} fontWeight={700} noWrap title={sale.name}>{sale.name}</Typography>
              <LinearProgress variant="determinate" value={sale.percentage} sx={{ display: { xs: "none", sm: "block" }, height: 10, borderRadius: 6, bgcolor: dark ? "#293347" : "#eef1f6", "& .MuiLinearProgress-bar": { bgcolor: sale.color, borderRadius: 6 } }} />
              <Typography fontSize={12.5} fontWeight={800} textAlign={{ xs: "left", sm: "right" }} sx={{ gridColumn: { xs: "2", sm: "auto" } }}>{formatNumber(sale.quantity)} units</Typography>
            </Box>
          ))}
          {!sales.length && <Typography color={muted} fontSize={12} textAlign="center" py={5}>No sales were recorded for this period.</Typography>}
        </Stack>
      </Box>

      <Box sx={{ ...card, overflow: "hidden" }}>
        <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "stretch", sm: "center" }} spacing={2} p={2.5}>
          <Box><Typography fontSize={17} fontWeight={800}>Inventory items</Typography><Typography color={muted} fontSize={12.5}>Live product stock · {selectedWarehouseName}</Typography></Box>
          <TextField value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search products..." size="small" sx={{ width: { xs: "100%", sm: 240 }, "& .MuiOutlinedInput-root": { borderRadius: "10px", fontSize: 12 } }} InputProps={{ startAdornment: <InputAdornment position="start"><SearchRoundedIcon sx={{ color: muted, fontSize: 19 }} /></InputAdornment> }} />
        </Stack>
        <Box sx={{ overflowX: "auto" }}>
          <Box sx={{ minWidth: 740 }}>
            <Box sx={{ display: "grid", gridTemplateColumns: "2.1fr 1.1fr .8fr 1fr .7fr 32px", px: 2.5, py: 1.25, bgcolor: dark ? "#202a3d" : "#f8fafc", color: muted }}>
              {["PRODUCT", "CATEGORY", "STOCK", "STATUS", "PRICE", ""].map((label, index) => <Typography key={`${label}-${index}`} fontSize={10.5} fontWeight={800} letterSpacing=".45px">{label}</Typography>)}
            </Box>
            {filteredInventory.map((item, index) => (
              <Box key={item.sku} sx={{ display: "grid", gridTemplateColumns: "2.1fr 1.1fr .8fr 1fr .7fr 32px", alignItems: "center", px: 2.5, py: 1.45, borderTop: `1px solid ${dark ? "#273146" : "#eef1f5"}`, "&:hover": { bgcolor: dark ? "rgba(255,255,255,.025)" : "#fafbff" } }}>
                <Stack direction="row" alignItems="center" spacing={1.4}><Avatar variant="rounded" sx={{ bgcolor: `${productColors[index % productColors.length]}18`, color: productColors[index % productColors.length], width: 38, height: 38, borderRadius: "10px" }}><Inventory2RoundedIcon sx={{ fontSize: 18 }} /></Avatar><Box><Typography fontSize={12.5} fontWeight={800}>{item.name}</Typography><Typography fontSize={10.5} color={muted}>{item.sku}</Typography></Box></Stack>
                <Typography fontSize={12} color={muted}>{item.category}</Typography>
                <Button
                  variant="text"
                  size="small"
                  onClick={() => viewWarehouseStock(item)}
                  aria-label={`View warehouse stock for ${item.name}`}
                  title="View stock by warehouse"
                  sx={{ justifySelf: "start", minWidth: 0, px: 0.75, py: 0.25, fontSize: 12.5, fontWeight: 900, textTransform: "none", textDecoration: "underline", textUnderlineOffset: "3px" }}
                >
                  {formatNumber(item.stock)}
                </Button>
                <Chip label={item.status} size="small" sx={{ justifySelf: "start", height: 24, fontSize: 10.5, fontWeight: 700, color: item.status === "In stock" ? "#059669" : item.status === "Low stock" ? "#d97706" : "#e11d48", bgcolor: item.status === "In stock" ? "#d1fae5" : item.status === "Low stock" ? "#fef3c7" : "#ffe4e6" }} />
                <Typography fontSize={12.5} fontWeight={700}>{formatCurrency(item.price)}</Typography>
                <IconButton size="small" onClick={() => viewStockMovement(item)} aria-label={`View stock movement for ${item.name}`} title="View stock movement"><TimelineRoundedIcon fontSize="small" /></IconButton>
              </Box>
            ))}
            {!filteredInventory.length && <Typography textAlign="center" color={muted} py={5}>No inventory items match your search.</Typography>}
          </Box>
        </Box>
      </Box>

      <Dialog open={Boolean(stockItem)} onClose={() => setStockItem(null)} fullWidth maxWidth="md" PaperProps={{ sx: { borderRadius: "18px", bgcolor: dark ? "#182033" : "#fff", backgroundImage: "none" } }}>
        <DialogTitle sx={{ p: 2.5, pb: 1.5 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
            <Stack direction="row" spacing={1.3} alignItems="center">
              <Avatar variant="rounded" sx={{ bgcolor: dark ? "#6366f122" : "#eef2ff", color: "#6366f1", width: 42, height: 42, borderRadius: "11px" }}><Inventory2RoundedIcon /></Avatar>
              <Box>
                <Typography fontSize={19} fontWeight={900}>Item stock by warehouse</Typography>
                <Typography color={muted} fontSize={12.5}>{stockItem?.name} · {stockItem?.sku}</Typography>
              </Box>
            </Stack>
            <IconButton onClick={() => setStockItem(null)} aria-label="Close warehouse stock details"><CloseRoundedIcon /></IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ px: 0, pb: 2.5 }}>
          {warehouseStockError && <Alert severity="error" sx={{ mx: 2.5, mb: 2, borderRadius: "10px" }}>{warehouseStockError}</Alert>}
          {warehouseStockLoading ? (
            <Stack alignItems="center" py={7} spacing={1.5}><CircularProgress size={30} /><Typography color={muted} fontSize={12.5}>Loading stock from every warehouse…</Typography></Stack>
          ) : (
            <>
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(4,1fr)" }, gap: 1.25, mx: 2.5, mb: 2.5 }}>
                {[
                  ["Category", stockItemInfo?.category || stockItem?.category || "—"],
                  ["Unit", stockItemInfo?.unit || "—"],
                  ["Selling price", formatCurrency(stockItemInfo?.price ?? stockItem?.price)],
                  ["Stock as of", dateRange.dateTo],
                ].map(([label, value]) => <Box key={label} sx={{ p: 1.5, borderRadius: "11px", bgcolor: dark ? "#202a3d" : "#f8fafc" }}><Typography color={muted} fontSize={10.5} fontWeight={700}>{label}</Typography><Typography mt={0.35} fontSize={12.5} fontWeight={800}>{value}</Typography></Box>)}
              </Box>
              <Stack direction="row" spacing={1} px={2.5} pb={2} flexWrap="wrap">
                <Chip label={`${formatNumber(warehouseStockTotals.stock)} total units`} sx={{ bgcolor: dark ? "#6366f122" : "#eef2ff", color: dark ? "#a5b4fc" : "#4f46e5", fontWeight: 800 }} />
                <Chip label={`${formatCurrency(warehouseStockTotals.value)} inventory value`} sx={{ bgcolor: dark ? "#10b98122" : "#d1fae5", color: dark ? "#6ee7b7" : "#047857", fontWeight: 800 }} />
              </Stack>
              <Box sx={{ overflowX: "auto" }}>
                <Box sx={{ minWidth: 690 }}>
                  <Box sx={{ display: "grid", gridTemplateColumns: ".8fr 1.7fr .75fr 1fr 1fr", px: 2.5, py: 1.2, bgcolor: dark ? "#202a3d" : "#f8fafc", color: muted }}>
                    {[
                      "CODE", "WAREHOUSE", "STOCK", "AVG. COST", "INVENTORY VALUE",
                    ].map((label, index) => <Typography key={label} fontSize={10.5} fontWeight={800} letterSpacing=".45px" textAlign={index >= 2 ? "right" : "left"}>{label}</Typography>)}
                  </Box>
                  {warehouseStocks.map((warehouse) => (
                    <Box key={warehouse.warehouseCode} sx={{ display: "grid", gridTemplateColumns: ".8fr 1.7fr .75fr 1fr 1fr", alignItems: "center", px: 2.5, py: 1.45, borderTop: `1px solid ${dark ? "#273146" : "#eef1f5"}` }}>
                      <Typography fontSize={12} fontWeight={800}>{warehouse.warehouseCode}</Typography>
                      <Typography fontSize={12.5}>{warehouse.warehouseName || "—"}</Typography>
                      <Typography fontSize={13} fontWeight={900} textAlign="right" color={Number(warehouse.stock) <= 0 ? muted : "#4f46e5"}>{formatNumber(warehouse.stock)}</Typography>
                      <Typography fontSize={12.5} textAlign="right">{formatCurrency(warehouse.averageCost)}</Typography>
                      <Typography fontSize={12.5} fontWeight={800} textAlign="right">{formatCurrency(warehouse.inventoryValue)}</Typography>
                    </Box>
                  ))}
                  {!warehouseStocks.length && !warehouseStockError && <Typography textAlign="center" color={muted} py={6}>No warehouses are configured for this branch.</Typography>}
                </Box>
              </Box>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={inventoryValueOpen} onClose={() => setInventoryValueOpen(false)} fullWidth maxWidth="md" PaperProps={{ sx: { borderRadius: "18px", bgcolor: dark ? "#182033" : "#fff", backgroundImage: "none" } }}>
        <DialogTitle sx={{ p: 2.5, pb: 1.5 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
            <Box>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Avatar variant="rounded" sx={{ bgcolor: dark ? "#0ea5e922" : "#e0f2fe", color: "#0284c7", width: 36, height: 36, borderRadius: "10px" }}><ShoppingCartCheckoutRoundedIcon sx={{ fontSize: 20 }} /></Avatar>
                <Typography fontSize={19} fontWeight={800}>Inventory value details</Typography>
              </Stack>
              <Typography color={muted} fontSize={12.5} mt={1}>Item stocks and values in {selectedWarehouseName.toLowerCase()} as of {dateRange.dateTo}.</Typography>
            </Box>
            <IconButton onClick={() => setInventoryValueOpen(false)} aria-label="Close inventory value details"><CloseRoundedIcon /></IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ px: 0, pb: 2.5 }}>
          <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "stretch", sm: "center" }} spacing={1.5} px={2.5} pb={2}>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              <Chip label={`${formatNumber(inventoryValueItems.length)} items`} sx={{ bgcolor: dark ? "#6366f122" : "#eef2ff", color: dark ? "#a5b4fc" : "#4f46e5", fontWeight: 800 }} />
              <Chip label={`${formatCurrency(displayedInventoryValue)} total value`} sx={{ bgcolor: dark ? "#0ea5e922" : "#e0f2fe", color: dark ? "#7dd3fc" : "#0369a1", fontWeight: 800 }} />
            </Stack>
            <Stack direction="row" spacing={1}>
              <Button disabled={!inventoryValueItems.length} onClick={exportInventoryValueToExcel} size="small" variant="outlined" startIcon={<FileDownloadRoundedIcon />} sx={{ textTransform: "none", borderRadius: "9px" }}>Export Excel</Button>
              <Button disabled={!inventoryValueItems.length} onClick={printInventoryValueReport} size="small" variant="contained" startIcon={<PrintRoundedIcon />} sx={{ bgcolor: "#0284c7", textTransform: "none", borderRadius: "9px", boxShadow: "none", "&:hover": { bgcolor: "#0369a1" } }}>Print</Button>
            </Stack>
          </Stack>
          <Box sx={{ overflowX: "auto" }}>
            <Box sx={{ minWidth: 680 }}>
              <Box sx={{ display: "grid", gridTemplateColumns: "2fr 1.25fr .75fr 1fr", px: 2.5, py: 1.2, bgcolor: dark ? "#202a3d" : "#f8fafc", color: muted }}>
                {["ITEM", "CATEGORY", "STOCK", "AMOUNT (VALUE)"].map((label, index) => <Typography key={label} fontSize={10.5} fontWeight={800} letterSpacing=".45px" textAlign={index >= 2 ? "right" : "left"}>{label}</Typography>)}
              </Box>
              {inventoryValueItems.map((item, index) => (
                <Box key={item.sku} sx={{ display: "grid", gridTemplateColumns: "2fr 1.25fr .75fr 1fr", alignItems: "center", px: 2.5, py: 1.45, borderTop: `1px solid ${dark ? "#273146" : "#eef1f5"}` }}>
                  <Stack direction="row" alignItems="center" spacing={1.2} minWidth={0}>
                    <Avatar variant="rounded" sx={{ bgcolor: `${productColors[index % productColors.length]}18`, color: productColors[index % productColors.length], width: 36, height: 36, borderRadius: "9px" }}><Inventory2RoundedIcon sx={{ fontSize: 17 }} /></Avatar>
                    <Box minWidth={0}><Typography fontSize={12.5} fontWeight={800} noWrap title={item.name}>{item.name}</Typography><Typography fontSize={10.5} color={muted}>{item.sku}</Typography></Box>
                  </Stack>
                  <Typography fontSize={12} color={muted}>{item.category || "Uncategorized"}</Typography>
                  <Typography fontSize={13} fontWeight={800} textAlign="right">{formatNumber(item.stock)}</Typography>
                  <Typography fontSize={12.5} fontWeight={800} textAlign="right" color="#0284c7">{formatCurrency(item.inventoryValue)}</Typography>
                </Box>
              ))}
              {!inventoryValueItems.length && <Typography textAlign="center" color={muted} py={6}>No inventory-value items are available for this warehouse selection.</Typography>}
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      <Dialog open={lowStockOpen} onClose={() => setLowStockOpen(false)} fullWidth maxWidth="md" PaperProps={{ sx: { borderRadius: "18px", bgcolor: dark ? "#182033" : "#fff", backgroundImage: "none" } }}>
        <DialogTitle sx={{ p: 2.5, pb: 1.5 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
            <Box>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Avatar variant="rounded" sx={{ bgcolor: dark ? "#f59e0b22" : "#fef3c7", color: "#d97706", width: 36, height: 36, borderRadius: "10px" }}><WarningAmberRoundedIcon sx={{ fontSize: 20 }} /></Avatar>
                <Typography fontSize={19} fontWeight={800}>Low-stock details</Typography>
              </Stack>
              <Typography color={muted} fontSize={12.5} mt={1}>Items with 20 units or fewer in {selectedWarehouseName.toLowerCase()}.</Typography>
            </Box>
            <IconButton onClick={() => setLowStockOpen(false)} aria-label="Close low-stock details"><CloseRoundedIcon /></IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ px: 0, pb: 2.5 }}>
          <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "stretch", sm: "center" }} spacing={1.5} px={2.5} pb={2}>
            <Stack direction="row" spacing={1}>
              <Chip size="small" label={`${lowStockItems.filter((item) => Number(item.stock) > 0).length} low stock`} sx={{ bgcolor: "#fef3c7", color: "#b45309", fontWeight: 700 }} />
              <Chip size="small" label={`${lowStockItems.filter((item) => Number(item.stock) <= 0).length} out of stock`} sx={{ bgcolor: "#ffe4e6", color: "#be123c", fontWeight: 700 }} />
            </Stack>
            <Stack direction="row" spacing={1}>
              <Button disabled={!lowStockItems.length} onClick={exportLowStockToExcel} size="small" variant="outlined" startIcon={<FileDownloadRoundedIcon />} sx={{ textTransform: "none", borderRadius: "9px" }}>Export Excel</Button>
              <Button disabled={!lowStockItems.length} onClick={printLowStockReport} size="small" variant="contained" startIcon={<PrintRoundedIcon />} sx={{ bgcolor: "#5b5ce2", textTransform: "none", borderRadius: "9px", boxShadow: "none", "&:hover": { bgcolor: "#4f46c8" } }}>Print</Button>
            </Stack>
          </Stack>
          <Box sx={{ overflowX: "auto" }}>
            <Box sx={{ minWidth: 650 }}>
              <Box sx={{ display: "grid", gridTemplateColumns: "2fr 1.2fr .7fr 1fr .8fr", px: 2.5, py: 1.2, bgcolor: dark ? "#202a3d" : "#f8fafc", color: muted }}>
                {["PRODUCT", "CATEGORY", "STOCK", "STATUS", "PRICE"].map((label) => <Typography key={label} fontSize={10.5} fontWeight={800} letterSpacing=".45px">{label}</Typography>)}
              </Box>
              {lowStockItems.map((item, index) => (
                <Box key={item.sku} sx={{ display: "grid", gridTemplateColumns: "2fr 1.2fr .7fr 1fr .8fr", alignItems: "center", px: 2.5, py: 1.5, borderTop: `1px solid ${dark ? "#273146" : "#eef1f5"}` }}>
                  <Stack direction="row" alignItems="center" spacing={1.2}>
                    <Avatar variant="rounded" sx={{ bgcolor: `${productColors[index % productColors.length]}18`, color: productColors[index % productColors.length], width: 36, height: 36, borderRadius: "9px" }}><Inventory2RoundedIcon sx={{ fontSize: 17 }} /></Avatar>
                    <Box><Typography fontSize={12.5} fontWeight={800}>{item.name}</Typography><Typography fontSize={10.5} color={muted}>{item.sku}</Typography></Box>
                  </Stack>
                  <Typography fontSize={12} color={muted}>{item.category}</Typography>
                  <Typography fontSize={13} fontWeight={800} color={Number(item.stock) <= 0 ? "#e11d48" : "#d97706"}>{formatNumber(item.stock)}</Typography>
                  <Chip label={Number(item.stock) <= 0 ? "Out of stock" : "Low stock"} size="small" sx={{ justifySelf: "start", height: 24, fontSize: 10.5, fontWeight: 700, color: Number(item.stock) <= 0 ? "#e11d48" : "#d97706", bgcolor: Number(item.stock) <= 0 ? "#ffe4e6" : "#fef3c7" }} />
                  <Typography fontSize={12.5} fontWeight={700}>{formatCurrency(item.price)}</Typography>
                </Box>
              ))}
              {!lowStockItems.length && <Typography textAlign="center" color={muted} py={6}>There are no low-stock items for this warehouse selection.</Typography>}
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(movementItem)} onClose={() => setMovementItem(null)} fullWidth maxWidth="md" PaperProps={{ sx: { borderRadius: "18px", bgcolor: dark ? "#182033" : "#fff", backgroundImage: "none" } }}>
        <DialogTitle sx={{ p: 2.5, pb: 1.5 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
            <Stack direction="row" spacing={1.3} alignItems="center">
              <Avatar variant="rounded" sx={{ bgcolor: dark ? "#6366f122" : "#eef2ff", color: "#6366f1", width: 40, height: 40, borderRadius: "10px" }}><TimelineRoundedIcon /></Avatar>
              <Box>
                <Typography fontSize={19} fontWeight={800}>Stock movement</Typography>
                <Typography color={muted} fontSize={12.5}>{movementItem?.name} · {movementItem?.sku}</Typography>
              </Box>
            </Stack>
            <IconButton onClick={() => setMovementItem(null)} aria-label="Close stock movement"><CloseRoundedIcon /></IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ px: 0, pb: 2.5 }}>
          <Stack direction={{ xs: "column", sm: "row" }} alignItems={{ xs: "stretch", sm: "flex-end" }} spacing={1.25} px={2.5} pb={2}>
            <Select
              value={movementWarehouseCode}
              onChange={(event) => updateMovementWarehouse(event.target.value)}
              disabled={movementLoading}
              displayEmpty
              size="small"
              aria-label="Filter stock movement by warehouse"
              sx={{ minWidth: 190, borderRadius: "10px", fontSize: 12, minHeight: 40 }}
            >
              <MenuItem value="">All warehouses</MenuItem>
              {warehouses.map((warehouse) => (
                <MenuItem key={warehouse.Code} value={String(warehouse.Code)}>
                  {warehouse.Description ? `[${warehouse.Code}] ${warehouse.Description}` : warehouse.Code}
                </MenuItem>
              ))}
            </Select>
            <TextField
              label="From"
              type="date"
              value={movementDateFrom}
              onChange={(event) => setMovementDateFrom(event.target.value)}
              size="small"
              InputLabelProps={{ shrink: true }}
              inputProps={{ max: movementDateTo || undefined }}
              sx={{ minWidth: 155, "& .MuiOutlinedInput-root": { borderRadius: "10px", fontSize: 12 } }}
            />
            <TextField
              label="To"
              type="date"
              value={movementDateTo}
              onChange={(event) => setMovementDateTo(event.target.value)}
              size="small"
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: movementDateFrom || undefined }}
              sx={{ minWidth: 155, "& .MuiOutlinedInput-root": { borderRadius: "10px", fontSize: 12 } }}
            />
            <Button
              variant="contained"
              disabled={movementLoading}
              onClick={updateStockMovementDates}
              sx={{ bgcolor: "#5b5ce2", textTransform: "none", borderRadius: "10px", minHeight: 40, boxShadow: "none", "&:hover": { bgcolor: "#4f46c8" } }}
            >
              Update
            </Button>
            <Button
              variant="outlined"
              disabled={movementLoading || !itemMovements.length}
              onClick={printStockMovement}
              startIcon={<PrintRoundedIcon />}
              sx={{ textTransform: "none", borderRadius: "10px", minHeight: 40 }}
            >
              Print
            </Button>
            <Typography color={muted} fontSize={12} sx={{ pb: { sm: 1 }, ml: { sm: "auto !important" } }}>{movementWarehouseName}</Typography>
          </Stack>
          {movementError && <Alert severity="error" sx={{ mx: 2.5, mb: 2, borderRadius: "10px" }}>{movementError}</Alert>}
          {movementLoading ? (
            <Stack alignItems="center" py={6} spacing={1.5}><CircularProgress size={28} /><Typography color={muted} fontSize={12}>Loading stock movement…</Typography></Stack>
          ) : (
            <Box sx={{ overflowX: "auto" }}>
              <Box sx={{ minWidth: 980 }}>
                <Box sx={{ display: "grid", gridTemplateColumns: "1.05fr 1.25fr 1.7fr 1.1fr 1fr .75fr 1fr", px: 2.5, py: 1.2, bgcolor: dark ? "#202a3d" : "#f8fafc", color: muted }}>
                  {["DATE", "DOCUMENT NO.", "NAME", "WAREHOUSE", "MOVEMENT", "QUANTITY", "UNIT COST"].map((label) => <Typography key={label} fontSize={10.5} fontWeight={800} letterSpacing=".45px">{label}</Typography>)}
                </Box>
                {itemMovements.map((movement, index) => {
                  const incoming = isIncomingMovement(movement.movementType);
                  return (
                    <Box key={`${movement.movementDate}-${movement.documentNumber}-${movement.warehouse}-${index}`} sx={{ display: "grid", gridTemplateColumns: "1.05fr 1.25fr 1.7fr 1.1fr 1fr .75fr 1fr", alignItems: "center", px: 2.5, py: 1.45, borderTop: `1px solid ${dark ? "#273146" : "#eef1f5"}` }}>
                      <Typography fontSize={12}>{new Date(`${movement.movementDate}T00:00:00`).toLocaleDateString()}</Typography>
                      <Typography fontSize={12} fontWeight={700}>{movement.documentNumber || "—"}</Typography>
                      <Typography fontSize={12} color={muted} noWrap title={movement.tradeName || ""}>{movement.tradeName || "—"}</Typography>
                      <Typography fontSize={12} color={muted}>{movement.warehouse || "—"}</Typography>
                      <Chip label={incoming ? "Stock in" : "Stock out"} size="small" sx={{ justifySelf: "start", height: 24, fontSize: 10.5, fontWeight: 700, color: incoming ? "#059669" : "#e11d48", bgcolor: incoming ? "#d1fae5" : "#ffe4e6" }} />
                      <Typography fontSize={12.5} fontWeight={800} color={incoming ? "#059669" : "#e11d48"}>{incoming ? "+" : "-"}{formatNumber(movement.quantity)}</Typography>
                      <Typography fontSize={12.5} fontWeight={700}>{formatCurrency(movement.unitCost)}</Typography>
                    </Box>
                  );
                })}
                {!itemMovements.length && !movementError && <Typography textAlign="center" color={muted} py={6}>No stock movement was recorded during this period.</Typography>}
              </Box>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default DashboardInvty;
